import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@pantry/ui';
import type { MemberWithUser, HouseholdRole } from '@pantry/api-client';
import {
  EllipsisVerticalIcon,
  ShieldCheckIcon,
  UserMinusIcon,
  ArrowRightStartOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { ChangeMemberRoleDialog } from './ChangeMemberRoleDialog';
import { RemoveMemberDialog } from './RemoveMemberDialog';
import { TransferOwnershipDialog } from './TransferOwnershipDialog';

interface MemberCardProps {
  householdId: string;
  member: MemberWithUser;
  currentUserId: string;
  currentUserRole: HouseholdRole;
}

function getRoleBadgeStyles(role: HouseholdRole) {
  switch (role) {
    case 'owner':
      return 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20';
    case 'admin':
      return 'bg-primary/10 text-primary border border-primary/20';
    default:
      return 'bg-muted text-muted-foreground border border-border/50';
  }
}

function getRoleLabel(role: HouseholdRole, t: ReturnType<typeof useLingui>['t']): string {
  switch (role) {
    case 'owner':
      return t`Owner`;
    case 'admin':
      return t`Admin`;
    case 'member':
      return t`Member`;
    default:
      return role;
  }
}

export function MemberCard({
  householdId,
  member,
  currentUserId,
  currentUserRole,
}: MemberCardProps) {
  const { t } = useLingui();
  const [showChangeRole, setShowChangeRole] = useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);

  const isCurrentUser = member.userId === currentUserId;
  const isOwner = currentUserRole === 'owner';
  const isAdmin = currentUserRole === 'admin' || isOwner;
  const isMemberOwner = member.role === 'owner';
  const isMemberAdmin = member.role === 'admin';

  // Determine what actions are available
  const canChangeRole = isOwner && !isCurrentUser && !isMemberOwner;
  const canRemove = isAdmin && !isCurrentUser && !isMemberOwner && (!isMemberAdmin || isOwner);
  const canTransferOwnership = isOwner && !isCurrentUser;
  const hasActions = canChangeRole || canRemove || canTransferOwnership;

  const userName = member.user.name || member.user.email;
  const userEmail = member.user.email;
  const initial = (member.user.name?.[0] || member.user.email[0])?.toUpperCase() || '?';

  return (
    <>
      <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary font-medium text-sm">
            {initial}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-foreground">
                {userName}
                {isCurrentUser && (
                  <span className="ml-1.5 text-xs text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded-md">
                    <Trans>you</Trans>
                  </span>
                )}
              </p>
            </div>
            {member.user.name && <p className="text-sm text-muted-foreground">{userEmail}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center rounded-lg px-2.5 py-1 text-xs font-medium ${getRoleBadgeStyles(member.role)}`}
          >
            {getRoleLabel(member.role, t)}
          </span>
          {hasActions && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg">
                  <EllipsisVerticalIcon className="h-4 w-4" />
                  <span className="sr-only">
                    <Trans>Member actions</Trans>
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl">
                {canChangeRole && (
                  <DropdownMenuItem onClick={() => setShowChangeRole(true)} className="rounded-lg">
                    <ShieldCheckIcon className="h-4 w-4 mr-2" />
                    <Trans>Change Role</Trans>
                  </DropdownMenuItem>
                )}
                {canTransferOwnership && (
                  <DropdownMenuItem onClick={() => setShowTransfer(true)} className="rounded-lg">
                    <ArrowRightStartOnRectangleIcon className="h-4 w-4 mr-2" />
                    <Trans>Transfer Ownership</Trans>
                  </DropdownMenuItem>
                )}
                {(canChangeRole || canTransferOwnership) && canRemove && <DropdownMenuSeparator />}
                {canRemove && (
                  <DropdownMenuItem
                    onClick={() => setShowRemove(true)}
                    className="text-destructive focus:text-destructive rounded-lg"
                  >
                    <UserMinusIcon className="h-4 w-4 mr-2" />
                    <Trans>Remove Member</Trans>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <ChangeMemberRoleDialog
        householdId={householdId}
        member={member}
        open={showChangeRole}
        onOpenChange={setShowChangeRole}
      />
      <RemoveMemberDialog
        householdId={householdId}
        member={member}
        open={showRemove}
        onOpenChange={setShowRemove}
      />
      <TransferOwnershipDialog
        householdId={householdId}
        member={member}
        open={showTransfer}
        onOpenChange={setShowTransfer}
      />
    </>
  );
}
